/*
 *  -----------------------------------------------------------------------
 *    <copyright file="multi-select-email/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect, ReactElement } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import { CompositeFilterDescriptor, filterBy, FilterDescriptor, FilterOperator } from '@progress/kendo-data-query';
import {
    ListItemProps,
    MultiSelect,
    MultiSelectChangeEvent,
    MultiSelectFilterChangeEvent,
    TagData,
} from '@progress/kendo-react-dropdowns';
import cn from 'clsx';
import '../styles.scss';
import DarkTooltip from '../../dark-tool-tip/dark-tool-tip';
import MultiSelectNoResults from '../multi-select-no-results';
import { MultiSelectEmailProps, Person } from '../multi-select.interface';
import { useBoolean } from 'hooks';

const MultiSelectEmail = ({
    people,
    onChange,
    label,
    customId,
    customClassName,
    placeholder,
    allowCustom = false,
    disabled = false,
}: MultiSelectEmailProps) => {
    const initialFilter = {
        logic: 'or',
        filters: [{ operator: FilterOperator.IsNotEmpty, value: '' }],
    } as CompositeFilterDescriptor;

    const [data, setData] = useState<Person[]>([]);
    const [filteredData, setFilteredData] = useState<Person[]>([]);
    const showPlaceholder = useBoolean(true);
    const isFocused = useBoolean(false);
    const [filter, setFilter] = useState<CompositeFilterDescriptor>(initialFilter);
    const [value, setValue] = useState<Person[]>([]);

    const validateEmail = (email: string) => {
        const emailReg = /^[\W]*[\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4}[\W]*$/;

        return emailReg.test(email.toLowerCase());
    };

    const getEmailList = (recipients: Person[]) => recipients.map((person: Person) => person.email).join(';');

    const getFilterValue = () => (filter.filters[0] as FilterDescriptor).value as string;

    const calculateEmailsFromFreeText = () => {
        const entries = getFilterValue()
            .split(';')
            .map((entry: string) => entry.trim());

        const peopleToAdd = [] as Person[];
        const remainingEntries = [...entries];
        const updatedData = [...data];

        entries.forEach((entry: string) => {
            const lowerCaseEntry = entry.toLowerCase();

            const removeFromRemainingEmails = () => {
                remainingEntries.splice(remainingEntries.indexOf(entry), 1);
            };

            const existingPersonIndex = data.findIndex(
                (person: Person) =>
                    person.email.toLowerCase() === lowerCaseEntry ||
                    `${person.firstName} ${person.lastName}`.toLowerCase() === lowerCaseEntry,
            );

            const shouldAddMemberEmail =
                existingPersonIndex > -1 && !peopleToAdd.some((item: Person) => item.email === lowerCaseEntry);

            if (shouldAddMemberEmail) {
                peopleToAdd.push(data[existingPersonIndex]);
                updatedData.splice(existingPersonIndex, 1);
                removeFromRemainingEmails();
                return;
            }

            const shouldAddNonMemberEmail =
                validateEmail(lowerCaseEntry) &&
                !value.some((item: Person) => item.email === lowerCaseEntry) &&
                !peopleToAdd.some((item: Person) => item.email === lowerCaseEntry);

            if (shouldAddNonMemberEmail) {
                peopleToAdd.push({
                    email: lowerCaseEntry,
                    firstName: '',
                    lastName: '',
                });
                removeFromRemainingEmails();
            }
        });

        return {
            peopleToAdd,
            remainingEntries,
            updatedData,
        };
    };

    const addEmailsFromFreeText = () => {
        const { peopleToAdd, remainingEntries, updatedData } = calculateEmailsFromFreeText();

        const updatedFilter = {
            ...filter,
            filters: (filter.filters as FilterDescriptor[]).map((filterDescriptor: FilterDescriptor) => ({
                ...filterDescriptor,
                value: remainingEntries.join(';'),
            })),
        };

        setFilter(updatedFilter);
        const newValue = [...value, ...peopleToAdd];
        onChange(getEmailList(newValue));
        setValue(newValue);
        setData(updatedData);
    };

    const updateData = (updatedValue: Person[]) => {
        const updatedValueIds = updatedValue.map((valueItem: Person) => valueItem.email);
        const newData = people.filter((dataItem: Person) => !updatedValueIds.includes(dataItem.email));

        setData(newData);
    };

    const updateFilteredData = (dataToFilter: Person[], updatedFilter: CompositeFilterDescriptor) => {
        setFilteredData(filterBy(dataToFilter.slice(), updatedFilter));
    };

    useEffect(() => {
        if (people) updateData(value);
    }, [people]);

    useEffect(() => {
        if (data) updateFilteredData(data, filter);
    }, [data]);

    const filterChange = (event: MultiSelectFilterChangeEvent) => {
        const spaceSeparatedValues = event.filter.value
            .split(' ')
            .filter((separatedValue: string) => separatedValue !== '');

        const invalidNameValues = [...spaceSeparatedValues];
        invalidNameValues.splice(0, 1);
        let invalidNameValue = invalidNameValues.join(' ');

        if (invalidNameValue === spaceSeparatedValues[1]) invalidNameValue = '';

        const fullNameFilter: CompositeFilterDescriptor = {
            logic: 'and',
            filters: [
                {
                    field: 'firstName',
                    operator: FilterOperator.EqualTo,
                    value: spaceSeparatedValues[0],
                },
                {
                    field: 'lastName',
                    operator: FilterOperator.StartsWith,
                    value: spaceSeparatedValues[1] ?? '',
                },
                {
                    field: 'lastName',
                    operator: FilterOperator.Contains,
                    value: invalidNameValue,
                },
            ],
        };

        const updatedFilter = {
            ...filter,
            filters: [
                { ...event.filter, field: 'email' },
                { ...event.filter, field: 'firstName' },
                { ...event.filter, field: 'lastName' },
                fullNameFilter,
            ],
        };

        updateFilteredData(data, updatedFilter);
        setFilter(updatedFilter);
    };

    const onFocus = () => {
        showPlaceholder.setFalse();
        isFocused.setTrue();
    };

    const onBlur = () => {
        addEmailsFromFreeText();
        showPlaceholder.setTrue();
        isFocused.setFalse();
    };

    const addEmail = (person: Person) => {
        const newValue = [...value, person];
        const newData = data.filter((item: Person) => item.email !== person.email);

        onChange(getEmailList(newValue));
        setValue(newValue);
        setData(newData);
        setFilter(initialFilter);
    };

    const removeEmail = (person: Person) => {
        const newValue = value.filter((item: Person) => item.email !== person.email);

        onChange(getEmailList(newValue));
        setValue(newValue);
        updateData(newValue);
    };

    const itemRender = (li: ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
        const onClick = () => {
            addEmail(itemProps.dataItem as Person);
        };

        if (!data.length) return <MultiSelectNoResults />;

        return (
            <button type="button" className="select-item__email" onClick={onClick}>
                <span>
                    {itemProps.dataItem.firstName} {itemProps.dataItem.lastName}
                </span>
                <span>{itemProps.dataItem.email}</span>
            </button>
        );
    };

    const tagRender = (tagData: TagData) => {
        const person = tagData.data[0] as Person;

        const onRemove = (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation(); // It's preventing the dropdown from opening when we remove the item
            removeEmail(person);
        };

        const displayText = () => {
            const text = `${person?.firstName || ''} ${person?.lastName || ''}`.trim();

            return text || person?.email || '';
        };

        return (
            <div key={person?.email || ''}>
                <DarkTooltip title={person?.email || ''} placement="top">
                    <p className="select-tag">
                        <span className="tag-text">{displayText()}</span>
                        <button type="button" className="remove-item-btn" onClick={onRemove}>
                            <FontAwesomeIcon
                                icon={['fal', 'times']}
                                className="fa-lg close-icon-cv"
                                data-testid="close-dialog"
                            />
                        </button>
                    </p>
                </DarkTooltip>
            </div>
        );
    };

    const listNoDataRender = () => (allowCustom ? '' : <MultiSelectNoResults />);

    return (
        <FormControl
            variant="outlined"
            className={cn(customClassName, 'multi-select', 'multi-select-email-wrap')}
            id={customId}
        >
            <span className={cn('multi-select-label', { focused: isFocused.value }, { disabled })}>{label}</span>
            <MultiSelect
                className="multi-select-email"
                ref={(instance) => {
                    (window as any)[customId || 'multiselect'] = instance;
                }}
                allowCustom={allowCustom}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={disabled}
                data={filteredData}
                onChange={(event: MultiSelectChangeEvent) => {
                    const newValue = event.target.value;
                    onChange(getEmailList(newValue));
                    setValue(newValue);
                    updateData(newValue);
                }}
                popupSettings={{ className: 'multi-select-popup' }}
                value={value}
                placeholder={placeholder}
                filterable
                filter={getFilterValue()}
                onFilterChange={filterChange}
                listNoDataRender={() => listNoDataRender()}
                itemRender={itemRender}
                tags={value.map((item) => ({ text: item.email, data: [item] }))}
                tagRender={tagRender}
            />
        </FormControl>
    );
};

export default MultiSelectEmail;
